import { renderEmbeddedEntry, renderMark, renderText } from "@2u-landing-pages/utils/contentful-richtext";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { Card as BricksCard } from "@red-digital/bricks";
import cn from "classnames";
import React from "react";
import { ContentfulImage } from "../../../ContentfulImage";
import { Icon } from "../../../Icon";
import { Initials } from "../../../Initials";
import cardStyles from "../../Card.module.scss";
import { CardVariantProps } from "../../Card.type";
import smallCardStyles from "./SmallCard.module.scss";

export const SmallCard: React.FC<CardVariantProps> = ({
  entry,
  heading,
  subhead,
  body,
  className,
  style,
  phoneNumber,
  parseMetadata,
  inspector,
}) => {
  const {
    initials,
    image,
    text_alignment = "center",
    icon,
    icon_size,
    image_config,
    heading: contentfulHeading,
    subhead: contentfulSubhead,
    body: contentfulBody,
  } = entry.fields;

  return (
    <BricksCard
      style={style}
      className={cn(cardStyles.card, cardStyles[`text-${text_alignment}`], smallCardStyles.small, className, {
        [smallCardStyles.initials]: Boolean(initials) && !image,
      })}
      layout={{
        breakpoint: "lg",
      }}
      slots={{
        start: {
          component: (
            <>
              {!image && !icon && initials && (
                <Initials
                  initials={initials}
                  size="small"
                  {...inspector({
                    entryId: entry.sys.id,
                    fieldId: "initials",
                  })}
                />
              )}
              {!image && icon && (
                <Icon
                  name={icon}
                  size={icon_size}
                  {...inspector({
                    entryId: entry.sys.id,
                    fieldId: "icon",
                  })}
                />
              )}
            </>
          ),
        },
      }}
      media={
        image?.fields?.file?.url
          ? {
              component: (
                <ContentfulImage
                  image={image}
                  config={image_config}
                  {...inspector({
                    entryId: entry.sys.id,
                    fieldId: "image",
                  })}
                  style={style}
                />
              ),
              position: "beforeContent",
            }
          : undefined
      }
      content={{
        headingProps: {
          variant: "heading-lg",
          className: cardStyles.heading,
          ...heading,
          children: contentfulHeading
            ? documentToReactComponents(contentfulHeading, {
                renderMark,
                renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
                renderNode: {
                  [BLOCKS.PARAGRAPH]: (node, children) => children,
                  [INLINES.EMBEDDED_ENTRY]: (node) =>
                    renderEmbeddedEntry(node, "Card Small Heading", contentfulHeading, phoneNumber, parseMetadata),
                },
              })
            : undefined,
          ...inspector({
            entryId: entry.sys.id,
            fieldId: "heading",
          }),
        },
        subheadProps: contentfulSubhead
          ? {
              variant: "subhead-md",
              className: cardStyles.subhead,
              ...subhead,
              children: documentToReactComponents(contentfulSubhead, {
                renderMark,
                renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
                renderNode: {
                  [BLOCKS.PARAGRAPH]: (node, children) => children,
                  [INLINES.EMBEDDED_ENTRY]: (node) =>
                    renderEmbeddedEntry(node, "Card Small Subhead", contentfulSubhead, phoneNumber, parseMetadata),
                },
              }),
              ...inspector({
                entryId: entry.sys.id,
                fieldId: "subhead",
              }),
            }
          : undefined,
        descriptionProps: {
          modifier: 500,
          variant: "body-md",
          className: cardStyles.body,
          ...body,
          type: "div",
          children: contentfulBody
            ? documentToReactComponents(contentfulBody, {
                renderMark,
                renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
                renderNode: {
                  [BLOCKS.PARAGRAPH]: (node, children) => children,
                  [INLINES.EMBEDDED_ENTRY]: (node) =>
                    renderEmbeddedEntry(node, "Card Small Body", contentfulBody, phoneNumber, parseMetadata),
                },
              })
            : undefined,
          ...inspector({
            entryId: entry.sys.id,
            fieldId: "body",
          }),
        },
      }}
    />
  );
};
